import type { Graphics } from "pixi.js";
import {
  PIECE_ASPECT,
  partBox,
  partColor,
  partKind,
  partSize,
  templateForParts,
} from "@talent/tasks";
import type { CardPainter } from "./choice-task.js";
import { COLOR, darken, lighten } from "./paint.js";

/**
 * The part/whole game's cards: the built thing above, the loose pieces below.
 *
 * **The target is drawn assembled and every option is drawn as a pile**, and the asymmetry is the
 * game rather than a presentation choice. An option laid out the way the figure is would be
 * answerable by putting two pictures on top of each other, which is a task about eyesight; a pile
 * can only be checked by taking the figure apart in your head first.
 *
 * The figure comes back from the pieces rather than travelling beside them: `templateForParts`
 * looks up the one figure made of exactly this set, which is why `parts.ts` keeps every template's
 * piece multiset unique and tests that it stayed that way. A pile that builds nothing — which is
 * every wrong answer — simply has no template and is drawn as a pile.
 */

/** The four piece colours, in the order `parts.ts` numbers them. */
const PIECE_COLOR: readonly (() => number)[] = [
  () => COLOR.sunny,
  () => COLOR.sky,
  () => COLOR.watermelon,
  () => COLOR.mint,
];

/**
 * One piece, centred on `(cx, cy)` and filling `w × h`.
 *
 * Every shape is drawn to the box it is given rather than to a radius, because the same piece
 * appears at one size inside a figure and at another in a pile, and a shape that changed proportion
 * between the two would be a different piece to a child.
 *
 * **Every piece is outlined**, and it is not decoration. These figures overlap by design — a
 * rocket's window sits on its body, a boat's sail crosses its mast — and without an outline two
 * touching pieces of the same colour merge into one blob, which in a game about counting an
 * inventory is the one thing that must not happen. The outline is the piece's own colour darkened
 * rather than a fixed ink, so a pale piece is not fenced in black.
 */
function drawPiece(
  g: Graphics,
  kind: number,
  color: number,
  cx: number,
  cy: number,
  w: number,
  h: number,
  flip: boolean,
): void {
  const hw = w / 2;
  const hh = h / 2;
  const face = color;
  const edge = { width: Math.max(2, Math.min(w, h) * 0.09), color: darken(color, 0.42) };
  const shine = { color: lighten(color, 0.5), alpha: 0.5 } as const;
  const up = flip ? -1 : 1;

  switch (kind) {
    case 0:
      g.ellipse(cx, cy, hw, hh).fill(face).stroke(edge);
      g.ellipse(cx - hw * 0.3, cy - hh * 0.32, hw * 0.3, hh * 0.24).fill(shine);
      break;
    case 1: {
      // Half-circle: a dome on a flat edge, and the edge is what a child matches on.
      const base = cy + hh * up;
      g.moveTo(cx - hw, base)
        .bezierCurveTo(cx - hw, base - hh * up * 2.6, cx + hw, base - hh * up * 2.6, cx + hw, base)
        .closePath()
        .fill(face)
        .stroke(edge);
      break;
    }
    case 2:
      g.poly([cx - hw, cy + hh * up, cx + hw, cy + hh * up, cx, cy - hh * up])
        .fill(face)
        .stroke(edge);
      break;
    case 3:
    case 4:
    case 5:
      g.roundRect(cx - hw, cy - hh, w, h, Math.min(w, h) * (kind === 3 ? 0.16 : 0.3))
        .fill(face)
        .stroke(edge);
      g.rect(cx - hw + w * 0.12, cy - hh + h * 0.14, w * 0.3, h * 0.16).fill(shine);
      break;
    default:
      // Trapezoid: the wide edge at the bottom unless flipped, so it reads as a bonnet one way and
      // a boat's hull the other.
      g.poly([
        cx - hw,
        cy + hh * up,
        cx + hw,
        cy + hh * up,
        cx + hw * 0.58,
        cy - hh * up,
        cx - hw * 0.58,
        cy - hh * up,
      ])
        .fill(face)
        .stroke(edge);
      break;
  }
}

/** Where the pieces of a pile sit: a small grid, in the order the stimulus lists them. */
function pileLayout(count: number): { cols: number; rows: number } {
  const cols = count <= 1 ? 1 : 2;
  return { cols, rows: Math.ceil(count / cols) };
}

export const paintPartsCard: CardPainter = (g, content, size, state) => {
  const parts = [...content];
  if (parts.length === 0) return;

  const colourOf = (part: number): number =>
    PIECE_COLOR[partColor(part)]?.() ?? COLOR.sunny;

  if (state === "target") {
    const template = templateForParts(parts);
    if (!template) return;
    // Template coordinates run -1..1 across the figure's box, so one unit is a bit under half the
    // card. The 0.42 leaves the margin that stops a wide figure touching the card's rounded corner.
    const unit = size * 0.42;
    for (const piece of template.pieces) {
      const box = partBox(piece.part);
      drawPiece(
        g,
        partKind(piece.part),
        colourOf(piece.part),
        piece.x * unit,
        piece.y * unit,
        box.w * unit,
        box.h * unit,
        piece.flip === true,
      );
    }
    return;
  }

  /**
   * A pile: the same pieces, one to a slot.
   *
   * **A piece keeps its PROPORTIONS exactly and has its SIZE compressed**, and the split is the
   * whole design of this view.
   *
   * Proportions are exact because getting them wrong is what made the first version unplayable: it
   * gave every kind a house size of its own, so a pile drew a mushroom's cap as a tall narrow dome
   * beside a fat slab of a stem while the figure above showed a wide shallow cap on a thin stick.
   * Nothing in the two pictures was the same object. The aspect now comes from `PIECE_ASPECT`, which
   * the figure reads too, so a bar lies down and a stick stands up in both.
   *
   * Size is compressed because honesty about it costs more than it buys. Drawing the pile at one
   * shared scale is faithful — a wheel really is a fifth of the bar it belongs to — and it turns a
   * wheel into an eight-pixel dot on a phone, which a child cannot identify at all. The three sizes
   * are mapped into a narrow band instead: the ordering stays visible, nothing disappears, and
   * nothing is lost, because no wrong pile is ever built by changing a size (see `flawed`).
   */
  const PILE_SIZE_HINT = [0.76, 0.88, 1.0];

  const { cols, rows } = pileLayout(parts.length);
  const pad = size * 0.11;
  const inner = size - pad * 2;
  const cellW = inner / cols;
  const cellH = inner / rows;

  parts.forEach((part, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    // A short last row is centred, so a three-piece pile does not sit lopsided in a 2×2.
    const inRow = Math.min(cols, parts.length - row * cols);
    const cx = -inner / 2 + (cols - inRow) * (cellW / 2) + col * cellW + cellW / 2;
    const cy = -inner / 2 + row * cellH + cellH / 2;

    const aspect = PIECE_ASPECT[partKind(part)] ?? { w: 1, h: 1 };
    const fit = Math.min((cellW * 0.84) / aspect.w, (cellH * 0.84) / aspect.h);
    const hint = PILE_SIZE_HINT[partSize(part)] ?? 1;
    drawPiece(
      g,
      partKind(part),
      colourOf(part),
      cx,
      cy,
      aspect.w * fit * hint,
      aspect.h * fit * hint,
      false,
    );
  });
};
